import React, { useEffect, useMemo, useState } from "react";
import api, { API_URL } from "@/lib/api";
import { WEBSITE_ORIGIN } from "@/lib/serviceMedia";
import { Button } from "@/components/ui/button";

/** Turn any stored media/path into an absolute URL the browser can load. */
export function resolvePreviewImageUrl(raw, siteOrigin = "") {
  if (!raw) return "";
  if (typeof raw === "object") {
    raw = raw.src || raw.url || raw.external_url || "";
  }
  let value = String(raw).trim();
  if (!value) return "";
  if (value.startsWith("//")) return `https:${value}`;
  if (/^https?:\/\//i.test(value)) return value;
  if (value.startsWith("/api/media/")) return `${API_URL}${value}`;
  if (value.startsWith("api/media/")) return `${API_URL}/${value}`;
  if (value.startsWith("/media/")) {
    return `${(siteOrigin || WEBSITE_ORIGIN).replace(/\/$/, "")}${encodeURI(value)}`;
  }
  if (/^[a-f0-9]{24}$/i.test(value)) return `${API_URL}/api/media/public/${value}`;
  if (value.startsWith("/")) {
    const base = (siteOrigin || WEBSITE_ORIGIN).replace(/\/$/, "");
    return `${base}${value}`;
  }
  return value;
}

/**
 * Always shows Draft + Live side by side (Google snippet, social card, live page iframe).
 */
export default function SeoLivePreview({
  path,
  draftSeo,
  fallbackTitle = "",
  fallbackDescription = "",
  fallbackImage = "",
  siteOrigin: siteOriginProp,
  liveData: liveDataProp,
  showPageFrame = true,
}) {
  const [live, setLive] = useState(liveDataProp || null);
  const [origin, setOrigin] = useState(siteOriginProp || "");
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("both"); // both | draft | live
  const [frameKey, setFrameKey] = useState(0);

  useEffect(() => {
    if (liveDataProp) setLive(liveDataProp);
  }, [liveDataProp]);

  useEffect(() => {
    if (siteOriginProp) {
      setOrigin(siteOriginProp.replace(/\/$/, ""));
      return;
    }
    let cancelled = false;
    api.get("/seo/settings")
      .then((r) => {
        if (!cancelled) setOrigin((r.data.production_origin || "").replace(/\/$/, ""));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [siteOriginProp]);

  const loadLive = () => {
    if (!path) {
      setLive(null);
      return;
    }
    setLoading(true);
    // Use shared API client (correct base URL + CORS) instead of a brittle env fetch.
    api
      .get(`/public/seo`, { params: { path } })
      .then((res) => setLive(res.data || null))
      .catch(() => {
        const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
        if (!base) {
          setLive(null);
          return null;
        }
        return fetch(`${base}/api/public/seo?path=${encodeURIComponent(path)}`)
          .then((r) => (r.ok ? r.json() : null))
          .then((data) => setLive(data))
          .catch(() => setLive(null));
      })
      .finally(() => {
        setLoading(false);
        setFrameKey((k) => k + 1);
      });
  };

  useEffect(() => {
    if (!liveDataProp) loadLive();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);

  const liveUrl = origin && path ? `${origin}${path === "/" ? "/" : path}` : "";

  const draft = useMemo(() => {
    const seo = draftSeo || {};
    const title = (seo.meta_title || fallbackTitle || "Untitled").trim() || "Untitled";
    const description = (seo.meta_description || fallbackDescription || "").trim();
    let canonical = (seo.canonical_url || "").trim();
    if (canonical && canonical.startsWith("/") && origin) {
      canonical = `${origin}${canonical}`;
    } else if (!canonical) {
      canonical = origin ? `${origin}${path === "/" ? "/" : path || ""}` : path || "";
    }
    const ogTitle = (seo.og_title || title).trim();
    const ogDescription = (seo.og_description || description).trim();
    const ogImage = resolvePreviewImageUrl(seo.og_image || fallbackImage || "", origin);
    return {
      title,
      description,
      canonical,
      ogTitle,
      ogDescription,
      ogImage,
      robots: seo.robots || "index,follow",
    };
  }, [draftSeo, fallbackTitle, fallbackDescription, fallbackImage, origin, path]);

  const liveCard = useMemo(() => {
    if (!live) {
      return {
        title: loading ? "Loading live SEO…" : "Nothing published yet",
        description: loading ? "" : "After you Save & Publish, this column shows what visitors see.",
        canonical: liveUrl || "—",
        ogTitle: "",
        ogDescription: "",
        ogImage: "",
        state: loading ? "loading" : "empty",
      };
    }
    return {
      title: live.title || "—",
      description: live.description || "—",
      canonical: live.canonical || liveUrl || "—",
      ogTitle: live.openGraph?.title || live.title || "—",
      ogDescription: live.openGraph?.description || live.description || "—",
      ogImage: resolvePreviewImageUrl(live.openGraph?.image || "", origin),
      state: live.publish_state || "published",
    };
  }, [live, liveUrl, loading, origin]);

  const showDraft = view === "both" || view === "draft";
  const showLive = view === "both" || view === "live";

  return (
    <div className="space-y-3 rounded-xl border border-cyan-500/20 bg-cyan-500/[0.04] p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-[0.14em] text-cyan-200/80 font-medium">Draft &amp; Live preview</p>
          <p className="text-[11px] text-white/45 mt-1 leading-relaxed max-w-xl">
            Left = your current edits (not live until publish). Right = what the site shows now.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {[
            { id: "both", label: "Both" },
            { id: "draft", label: "Draft only" },
            { id: "live", label: "Live only" },
          ].map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setView(t.id)}
              className={`px-2.5 h-8 rounded-lg text-xs border ${
                view === t.id
                  ? "bg-white/12 text-white border-cyan-400/40"
                  : "text-white/50 border-white/10 hover:text-white"
              }`}
            >
              {t.label}
            </button>
          ))}
          <Button size="sm" variant="ghost" className="text-white/60 h-8" onClick={loadLive}>
            Refresh live
          </Button>
          {liveUrl && (
            <a href={liveUrl} target="_blank" rel="noreferrer" className="text-[11px] text-cyan-300/90 hover:underline px-1">
              Open live ↗
            </a>
          )}
        </div>
      </div>

      <div className={`grid gap-3 ${view === "both" ? "grid-cols-1 lg:grid-cols-2" : "grid-cols-1"}`}>
        {showDraft && (
          <PreviewColumn
            eyebrow="Draft — before publish"
            accent="amber"
            title={draft.title}
            url={draft.canonical}
            description={draft.description}
            ogTitle={draft.ogTitle}
            ogDescription={draft.ogDescription}
            ogImage={draft.ogImage}
            imageFallback={liveCard.ogImage}
            badge={draft.robots?.includes("noindex") ? "Hidden from Google" : "Will index"}
            hint="Updates as you type in the form"
          />
        )}
        {showLive && (
          <PreviewColumn
            eyebrow="Live — published now"
            accent="emerald"
            title={liveCard.title}
            url={liveCard.canonical}
            description={liveCard.description}
            ogTitle={liveCard.ogTitle}
            ogDescription={liveCard.ogDescription}
            ogImage={liveCard.ogImage}
            badge={liveCard.state === "empty" ? "Not published" : `State: ${liveCard.state}`}
            hint="What Google / visitors see on the live site"
          />
        )}
      </div>

      {showPageFrame && showLive && liveUrl && (
        <div className="rounded-xl border border-emerald-400/20 overflow-hidden bg-black/30">
          <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-white/5">
            <p className="text-[10px] uppercase tracking-wider text-emerald-100/70">Live page preview</p>
            <p className="text-[10px] text-white/35 font-mono truncate max-w-[60%]">{liveUrl}</p>
          </div>
          <iframe
            key={frameKey}
            title="Live page preview"
            src={liveUrl}
            className="w-full h-[220px] sm:h-[320px] lg:h-[420px] bg-white"
            sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
          />
        </div>
      )}

      {showPageFrame && showDraft && !showLive && (
        <div className="rounded-xl border border-amber-400/20 bg-amber-400/5 px-4 py-3 text-xs text-amber-100/80">
          Draft can’t load the real site in an iframe until it’s published. Use the Draft column above for Google + social preview of your edits.
        </div>
      )}
    </div>
  );
}

function PreviewColumn({
  eyebrow, accent, title, url, description, ogTitle, ogDescription, ogImage, imageFallback, badge, hint,
}) {
  const ring = accent === "amber" ? "border-amber-400/35 bg-amber-400/[0.06]" : "border-emerald-400/35 bg-emerald-400/[0.06]";
  const eye = accent === "amber" ? "text-amber-100" : "text-emerald-100";
  const [imgSrc, setImgSrc] = useState(ogImage || "");
  const [imgFailed, setImgFailed] = useState(false);

  useEffect(() => {
    setImgSrc(ogImage || "");
    setImgFailed(false);
  }, [ogImage]);

  const onImgError = () => {
    if (imageFallback && imgSrc !== imageFallback) {
      setImgSrc(imageFallback);
      return;
    }
    setImgFailed(true);
  };

  return (
    <div className={`rounded-xl border ${ring} p-3 space-y-3 min-w-0`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className={`text-[11px] uppercase tracking-wider font-medium ${eye}`}>{eyebrow}</p>
          {hint && <p className="text-[10px] text-white/40 mt-0.5">{hint}</p>}
        </div>
        {badge && (
          <span className="text-[10px] text-white/55 shrink-0 px-2 py-0.5 rounded-full bg-black/30 border border-white/10">
            {badge}
          </span>
        )}
      </div>

      <div>
        <p className="text-[10px] uppercase tracking-wider text-white/35 mb-1.5">Google result</p>
        <div className="rounded-md bg-white p-3 space-y-1 min-w-0 shadow-sm">
          <p className="text-[11px] text-[#202124] truncate" title={url || ""}>{url || "—"}</p>
          <p className="text-[17px] text-[#1a0dab] leading-snug break-words" title={title || ""}>{title || "Untitled"}</p>
          <p className="text-[13px] text-[#4d5156] break-words leading-snug" title={description || ""}>
            {description || "No description"}
          </p>
        </div>
      </div>

      <div>
        <p className="text-[10px] uppercase tracking-wider text-white/35 mb-1.5">Social card</p>
        <div className="rounded-md border border-white/10 overflow-hidden bg-[#1b1f23]">
          {imgSrc && !imgFailed ? (
            <img src={imgSrc} alt="" className="w-full h-32 object-cover bg-black/40" onError={onImgError} />
          ) : (
            <div className="h-20 flex items-center justify-center text-[10px] text-white/35 px-2 text-center">
              {ogImage || imageFallback ? "Image failed to load — set Share image in SEO fields" : "No share image set"}
            </div>
          )}
          <div className="p-3 space-y-1 min-w-0">
            <p className="text-sm text-white font-medium break-words" title={ogTitle || title || ""}>
              {ogTitle || title || "—"}
            </p>
            <p className="text-xs text-white/55 break-words" title={ogDescription || description || ""}>
              {ogDescription || description || "—"}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
